// Browser-safe catalog of the Clarion Wire content-provenance gig: id, label,
// blurb, the data-entry field spec, and the conformance teaching hint.
//
// Mirrors the other catalogs in shape and role, for the single gig surfaced in
// the Clarion Wire app. It imports NOTHING heavy — no profile/schema JSON, no
// seal path, no Node built-ins — so both the browser UI and the /api/run
// Function can import it. It is the single source of truth for the gig id and
// input shape:
//   - the UI renders the story data-entry form from `fields`,
//   - the Clarion /api/run validates the submitted inputs against `fields`,
//   - src/live/gigs-newsroom.ts (the seal-bound gig) reuses this id.
// A test asserts every newsroom catalog id resolves to a gig so the two cannot
// drift.
//
// The seal-side gig reads: sources, signOff, storyRef. `sources` is a closed
// csv (allowlist) — "Reuters"/"Associated Press"/"County Water Board minutes"
// are on the mandate's allowlist; "Anonymous tip" is off-allowlist (a source
// violation). `signOff` is a closed select — "signed_off" conforms; "auto"
// (auto-published, no human editor sign-off) is the headline violation lever.
// The article text and prompt are hashed into the receipt, never sealed raw.

import type { GigCatalogEntry } from "./gig-catalog.js";

export const NEWSROOM_GIG_CATALOG: GigCatalogEntry[] = [
  {
    id: "content_provenance",
    label: "Newsroom: publish an AI-assisted story",
    blurb:
      "Clarion's newsroom agent drafts a story from an allowlist of sources and publishes it under an editorial mandate: a human editor must sign off first. It gathers sources, drafts, fact-checks, (optionally) gets the editor's sign-off, and publishes: one sealed content credential. The article text and the model prompt are hashed into the receipt, never sealed raw. Enter a story and run it: the agent seals a real Sequesign credential server-side, then you verify it here in your browser.",
    usesReview: false,
    runLabel: "Seal the content credential",
    fields: [
      {
        // A closed set: "signed_off" means a human editor approved before
        // publish; "auto" means the agent published on its own (the headline
        // violation lever). Keep in sync with the gig's signOff handling.
        key: "signOff",
        label: "Publish path",
        type: "select",
        default: "signed_off",
        options: ["signed_off", "auto"],
        help: "“signed_off” = a human editor approved before publish. “auto” = auto-published with no editor sign-off (records conformant: false)."
      },
      {
        // A closed allowlist of sources (not a mere character filter): the
        // Function rejects any csv item not in `options`, so a source off the
        // allowlist is the other violation lever. Keep in sync with
        // ALLOWED_SOURCES in gigs-newsroom.ts.
        key: "sources",
        label: "Sources",
        type: "csv",
        default: "Reuters, Associated Press, County Water Board minutes",
        options: ["Reuters", "Associated Press", "County Water Board minutes", "Anonymous tip"],
        help: "Comma-separated. Allowlisted: Reuters, Associated Press, County Water Board minutes. “Anonymous tip” is off the allowlist."
      },
      {
        key: "storyRef",
        label: "Story reference",
        type: "text",
        default: "CW-2026-3391"
      }
    ],
    violationHint:
      "Set the publish path to “auto” (no editor sign-off), or add the off-allowlist “Anonymous tip” source, to seal a content credential that honestly records conformant: false."
  }
];

export function newsroomCatalogEntry(id: string | undefined): GigCatalogEntry | undefined {
  if (id === undefined) return NEWSROOM_GIG_CATALOG[0];
  return NEWSROOM_GIG_CATALOG.find((g) => g.id === id);
}
